package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ModalShell
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.standups.StandupsStore
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.template.AiStatusDto
import co.maytes.standupbot.shared.dto.template.TestFireRequestDto
import co.maytes.standupbot.shared.dto.template.TestFireResponseDto
import co.maytes.standupbot.shared.template.SectionsConfigDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch

@Composable
fun TestFireModal(
    workspaceId: String,
    sectionsConfig: SectionsConfigDto,
    teamsStore: StandupsStore,
    store: TemplatesStore,
    onDismiss: () -> Unit,
) {
    var selectedTeamId by remember { mutableStateOf<String?>(null) }
    var running by remember { mutableStateOf(false) }
    var outcome by remember { mutableStateOf<TestFireResponseDto?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(workspaceId) {
        teamsStore.list(workspaceId)
    }

    val standups = teamsStore.items.map { it.id to it.name }
    if (standups.isNotEmpty() && selectedTeamId == null) {
        selectedTeamId = standups.first().first
    }

    ModalShell(onDismiss = onDismiss, fullscreen = true) {
        TestFireHeader()
        TestFireBody(
            standups = standups,
            selectedTeamId = selectedTeamId,
            onSelectTeam = { selectedTeamId = it },
            outcome = outcome,
            error = error,
        )
        TestFireFooter(
            running = running,
            canRun = !running && selectedTeamId != null,
            onClose = onDismiss,
            onRun = {
                val standupId = selectedTeamId ?: return@TestFireFooter
                running = true
                error = null
                outcome = null
                scope.launch {
                    val res = store.testFire(
                        workspaceId = workspaceId,
                        body = TestFireRequestDto(
                            sectionsConfig = sectionsConfig,
                            standupId = standupId,
                        ),
                    )
                    running = false
                    when (res) {
                        is Result.Ok -> outcome = res.value
                        is Result.Err -> error = res.error.displayMessage
                    }
                }
            },
        )
    }
}

@Composable
private fun TestFireHeader() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "24px 24px 16px")
                property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("flex", "0 0 auto")
            },
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(Spacing.xxs)) {
            SpanText(
                text = "Test Fire",
                modifier = Modifier
                    .fontSize(Typography.h2.size)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = "Runs the selected standup's synthesis on its recent Linear activity. Your template text isn't applied yet. Real AI cost applies.",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary),
            )
        }
    }
}

@Composable
private fun TestFireBody(
    standups: List<Pair<String, String>>,
    selectedTeamId: String?,
    onSelectTeam: (String) -> Unit,
    outcome: TestFireResponseDto?,
    error: String?,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex", "1 1 auto")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("padding", "20px 24px")
            },
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            if (standups.isEmpty()) {
                SpanText(
                    text = "Create a standup first to run Test Fire.",
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.warning),
                )
            } else {
                TeamPicker(standups = standups, selectedTeamId = selectedTeamId, onSelect = onSelectTeam)
            }

            outcome?.let { ResultPanel(it) }
            error?.let {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.error),
                )
            }
        }
    }
}

@Composable
private fun TeamPicker(
    standups: List<Pair<String, String>>,
    selectedTeamId: String?,
    onSelect: (String) -> Unit,
) {
    Column(modifier = Modifier.styleModifier { property("max-width", "420px") }) {
        SpanText(
            text = "Standup",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .margin(bottom = Spacing.xxs),
        )
        Select(
            value = selectedTeamId ?: "",
            options = standups.map { (id, name) -> SelectOption(id, name) },
            onValueChange = onSelect,
        )
    }
}

@Composable
private fun ResultPanel(r: TestFireResponseDto) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        ResultHeader(r)
        if (r.costCapReached) {
            WarningBanner(COST_CAP_MESSAGE)
        } else {
            r.aiStatus?.let { WarningBanner(aiStatusMessage(it)) }
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .gap(Spacing.md)
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(360px, 1fr))")
                    property("align-items", "stretch")
                },
        ) {
            ResultBlock(
                heading = if (r.aiStatus == AiStatusDto.FELL_BACK) "Fallback output (no AI)" else "Output (standard prompt)",
                body = r.output.ifBlank { "(empty)" },
            )
            if (r.rawLinearActivity.isNotBlank()) {
                ResultBlock(
                    heading = "Linear activity in the window",
                    body = r.rawLinearActivity,
                    muted = true,
                )
            }
        }
    }
}

@Composable
private fun TestFireFooter(
    running: Boolean,
    canRun: Boolean,
    onClose: () -> Unit,
    onRun: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "16px 24px")
                property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("flex", "0 0 auto")
            },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End,
        ) {
            Button(label = "Close", variant = ButtonVariant.Secondary, onClick = onClose)
            Box(modifier = Modifier.margin(left = Spacing.sm)) {
                Button(
                    label = if (running) "Running…" else "Run",
                    variant = ButtonVariant.Primary,
                    enabled = canRun,
                    onClick = onRun,
                )
            }
        }
    }
}

@Composable
private fun ResultHeader(r: TestFireResponseDto) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        SpanText(
            text = "${r.inputTokens}in / ${r.outputTokens}out tokens · $${formatUsd(r.costUsd)}",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textSecondary),
        )
        SpanText(
            text = "· ${r.linearActivitySummaryLine}",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}

@Composable
private fun WarningBanner(message: String) {
    SpanText(
        text = message,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(Palette.warning),
    )
}

private fun aiStatusMessage(status: AiStatusDto): String =
    when (status) {
        AiStatusDto.FELL_BACK ->
            "⚠ AI synthesis was unavailable, so this is the non-AI fallback output. " +
                "Add your Anthropic key in Settings > Integrations."
        AiStatusDto.DEGRADED ->
            "⚠ AI synthesis failed for one or more people, so the output is partial. " +
                "If this persists, check your Anthropic key in Settings > Integrations."
    }

private const val COST_CAP_MESSAGE =
    "⚠ This workspace has reached its monthly AI budget. No AI call was made; showing the non-AI fallback output."

private fun formatUsd(dollars: Double): Double =
    (dollars * USD_DECIMALS).toInt() / USD_DECIMALS.toDouble()

private const val USD_DECIMALS = 1000

@Composable
private fun ResultBlock(heading: String, body: String, muted: Boolean = false) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.xxs),
    ) {
        SpanText(
            text = heading,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textSecondary)
                .styleModifier {
                    property("text-transform", "none")
                    property("letter-spacing", "0.06em")
                },
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundColor(if (muted) Palette.surfaceSubtle else Palette.surface)
                .padding(Spacing.md)
                .styleModifier {
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("border-radius", "${Radius.md.value}px")
                    property("white-space", "pre-wrap")
                    property("word-break", "break-word")
                    property("font-family", Typography.FONT_MONO)
                },
        ) {
            SpanText(
                text = body,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(if (muted) Palette.textSecondary else Palette.textPrimary),
            )
        }
    }
}
